import { css } from 'styled-components';
import { createStylesForBreakpoints, isBreakpointConfig, } from '../helpers/breakpoint';
import { createCSSVarName } from '../theme-provider/create-css-var-name';
const directions = [
    'top',
    'right',
    'bottom',
    'left',
    'inlineStart',
    'inlineEnd',
];
function isDirectionalConfig(prop) {
    return (typeof prop === 'object' &&
        Object.keys(prop).every((key) => directions.includes(key)));
}
// create CSS variables for all dynamic sizes for a given configuration (e.g. container gutter or padding)
export function createCSSVariablesForSizes(configOrSize, theme) {
    if (!configOrSize) {
        return '';
    }
    if (isDirectionalConfig(configOrSize)) {
        return directions
            .map((key) => {
            const directionalConfigOrSize = configOrSize[key];
            return createCSSVariablesForSizes(directionalConfigOrSize, theme);
        })
            .join('');
    }
    if (isBreakpointConfig(configOrSize)) {
        const cssVar = createCSSVarName(configOrSize, 'container');
        return createStylesForBreakpoints(configOrSize, (size) => {
            const value = theme.size[size];
            return `${cssVar}: ${value};`;
        });
    }
    return '';
}
function createSpacing(gutter, theme) {
    if (isBreakpointConfig(gutter)) {
        const cssVar = createCSSVarName(gutter, 'container');
        return `var(${cssVar})`;
    }
    return theme.size[gutter];
}
export function createHorizontalAlignmentStyles(horizontalAlign) {
    return createStylesForBreakpoints(horizontalAlign, (horizontalAlign) => `justify-content: ${horizontalAlign};`);
}
function wrapStyles(wrap, theme, gutter) {
    const spacing = (gutter && createSpacing(gutter, theme)) || '0px';
    switch (wrap) {
        case 'never':
            return `
				display: flex;
				flex-wrap: nowrap;
				margin: 0;
			`;
        case 'always':
            return `
				display: block;
				flex-wrap: unset;
				margin: 0;
			`;
        default: {
            return `
				display: flex;
				flex-wrap: wrap;
				margin: 0 calc(-0.5 * ${spacing}) calc(-1 * ${spacing});
			`;
        }
    }
}
export function createWrapStyles(theme, gutter, wrap = 'auto') {
    return css `${createStylesForBreakpoints(wrap, (wrap) => wrapStyles(wrap, theme, gutter))}`;
}
function spacingBetweenElementsStyles(wrap, theme, gutter, direction) {
    const spacing = (gutter && createSpacing(gutter, theme)) || '0px';
    switch (wrap) {
        case 'never':
            return `
				margin: 0 calc(${spacing} / 2);
				:first-child {
					${(theme.direction || direction) === 'rtl'
                ? `margin: 0 0 0 calc(${spacing} / 2);`
                : `margin: 0 calc(${spacing} / 2) 0 0;`}
				}
				:last-child {
					${(theme.direction || direction) === 'rtl'
                ? `margin: 0 calc(${spacing} / 2) 0 0;`
                : `margin: 0 0 0 calc(${spacing} / 2);`}
				}
			`;
        case 'always':
            return `
				margin: 0 0 ${spacing};
				:last-child {
					margin: 0;
				}
			`;
        default:
            return `
				margin: 0 calc(${spacing} / 2) ${spacing};
				:last-child {
					margin: 0 calc(${spacing} / 2) ${spacing};
				}
			`;
    }
}
export function createSpacingBetweenElementsStyles(props) {
    const { wrapped = 'auto', theme, gutter, direction } = props;
    return css `${createStylesForBreakpoints(wrapped, (wrap) => spacingBetweenElementsStyles(wrap, theme, gutter, direction))}`;
}
function createPaddings(value, key) {
    switch (key) {
        case 'inlineStart':
            return `
				padding-inline-start: ${value};
			`;
        case 'inlineEnd':
            return `
				padding-inline-end: ${value};
			`;
        default:
            return `padding-${key}: ${value};`;
    }
}
function createDirectionalSpacings(key, padding, theme) {
    const directionalPadding = padding[key];
    if (isBreakpointConfig(directionalPadding)) {
        const cssVar = createCSSVarName(directionalPadding, 'container');
        return createPaddings(`var(${cssVar})`, key);
    }
    if (typeof directionalPadding === 'string' && theme) {
        return createPaddings(theme.size[directionalPadding], key);
    }
    return '';
}
export function createSpacingAroundElementsStyles(padding, theme) {
    if (typeof padding === 'string' && theme) {
        return css `padding:${theme.size[padding]};`;
    }
    if (isDirectionalConfig(padding)) {
        return css `padding:0;${directions.reduce((acc, key) => {
            return `
						${acc}
						${createDirectionalSpacings(key, padding, theme)}
					`;
        }, '')};`;
    }
    if (isBreakpointConfig(padding)) {
        const cssVar = createCSSVarName(padding, 'container');
        return css `padding:var(${cssVar});`;
    }
    return css `padding:0;`;
}
